import React from 'react';
import { Modal, Table } from 'react-bootstrap';
import { IconArrowLeft, IconCheck, IconSearch, IconCategory } from '@tabler/icons-react';
import useIndexInventory from '../../services/inventory/hooks/useIndexInventory';
import Loader from './Loader';
import { formatPrice } from '../utils/formatPrice';

interface InventoryItem {
    id: string;
    item_name: string;
    price: string;
}

interface SelectInventoryModalProps {
    show: boolean;
    onClose: () => void;
    onSelect: (inventory: InventoryItem) => void;
}

interface CategoryInfo {
    id: string;
    name: string;
}

const SelectInventoryModal: React.FC<SelectInventoryModalProps> = ({ show, onClose, onSelect }) => {
    const [search, setSearch] = React.useState('');
    const [selectedCategory, setSelectedCategory] = React.useState<CategoryInfo | null>(null);

    const { data: inventoriesData, isLoading } = useIndexInventory({
        page: 1,
        limit: 100,
        search: search,
        include: 'category',
    });

    // Extract unique categories from inventory data
    const categories = React.useMemo(() => {
        if (!inventoriesData?.data) return [];
        const categoryMap = new Map<string, CategoryInfo>();
        inventoriesData.data.forEach((item) => {
            if (item.category) {
                categoryMap.set(item.category.id, {
                    id: item.category.id,
                    name: item.category.name,
                });
            }
        });
        return Array.from(categoryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [inventoriesData]);

    // Filter inventories by selected category
    const filteredInventories = React.useMemo(() => {
        if (!inventoriesData?.data || !selectedCategory) return [];
        return inventoriesData.data.filter((item) => item.category_id === selectedCategory.id);
    }, [inventoriesData, selectedCategory]);

    const handleSelect = (inventory: InventoryItem) => {
        onSelect(inventory);
        handleClose();
    };

    const handleClose = () => {
        onClose();
        setSearch('');
        setSelectedCategory(null);
    };

    const handleBack = () => {
        setSelectedCategory(null);
        setSearch('');
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {selectedCategory ? (
                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary border-0"
                                onClick={handleBack}
                            >
                                <IconArrowLeft size={18} />
                            </button>
                            <span>{selectedCategory.name}</span>
                        </div>
                    ) : (
                        'Select Category'
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <Loader />
                ) : !selectedCategory ? (
                    /* Step 1: Category Selection */
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {categories.length === 0 ? (
                            <div className="text-center text-muted py-4">
                                No categories found.
                            </div>
                        ) : (
                            <div className="list-group">
                                {categories.map((category) => {
                                    const count = inventoriesData?.data.filter(
                                        (item) => item.category_id === category.id
                                    ).length || 0;
                                    return (
                                        <button
                                            key={category.id}
                                            type="button"
                                            className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                                            onClick={() => setSelectedCategory(category)}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <IconCategory size={18} className="text-primary" />
                                                <span className="fw-medium">{category.name}</span>
                                            </div>
                                            <span className="badge bg-primary rounded-pill text-white">{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    /* Step 2: Template Selection within Category */
                    <>
                        {/* Search Input */}
                        <div className="mb-3">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <IconSearch size={18} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search inventory..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table hover className="mb-0">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th>Item Name</th>
                                        <th className="text-end">Price</th>
                                        <th style={{ width: '80px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredInventories.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="text-center text-muted py-4">
                                                No inventory items found in this category.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredInventories.map((inventory) => (
                                            <tr key={inventory.id} style={{ cursor: 'pointer' }} onClick={() => handleSelect(inventory)}>
                                                <td>{inventory.item_name}</td>
                                                <td className="text-end">{formatPrice(Number(inventory.price))}</td>
                                                <td className="text-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(inventory);
                                                        }}
                                                    >
                                                        <IconCheck size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default SelectInventoryModal;
